import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { combineReducers } from '@reduxjs/toolkit';
import { Reply } from '~/types/models/reply';
import { configureStore } from '~/utils/redux';
import { useMedia } from 'react-use-media';
import useAnalytics from '~/react-hooks/use-analytics';

import rootReducer from './reducers/root';
import videoReducer from './reducers/video';
import commentsReducer from './reducers/comments';
import trackingReducer from './reducers/tracking';

import Postcard from '~/Share/landingPage/Postcard';
import NextGen from '~/Share/landingPage/NextGen';
import {
  LandingPageIntent,
  LandingPageType,
  ReduxState,
  RootState,
} from '~/types/redux/share';
import { GREET_SHARED, GREET_VIEWED } from '~/Share/Analytics';
import {
  GREET_VIDEO_EMBED_PLAYED,
  GREET_CTA_CLICKED,
} from '~/generated/AnalyticsTypes';
import { createRestApi } from '~/App/Api/rest';
import { useQueryString } from '~/react-hooks/use-query-string';
import useNextGenTemplate from '~/react-hooks/use-nextgen-template';
import { FeatureDecisions, FeaturesContext } from '~/react-hooks/use-features';
import Mobile from './landingPage/Mobile';

declare global {
  const config: RootState & {
    showShareButton: boolean;
    replies: Reply[];
    features: FeatureDecisions;
  };
}
export type GreetLandingPageProps = {
  hideReport?: boolean;
};

export default function GreetLandingPage({
  hideReport = false,
}: GreetLandingPageProps) {
  const {
    model: greet,
    assignee,
    messageTemplate,
    lang,
    isRecipient,
    reactionCount,
    replies,
    showShareButton,
    features,
    replyByVideo,
    metadata,
    greetType,
    profile,
  } = config;

  const analytics = useAnalytics();
  const [store, setStore] = useState();

  const { intent } = useQueryString<{ intent: LandingPageIntent }>();
  const showSingleColumnLayout = useMedia({
    maxWidth: 850,
  });
  const showMobileLayout = useMedia({
    maxWidth: 638,
  });

  const showNextGenLayout = useNextGenTemplate({
    canAccessNextGenTemplate: features.canAccessNextGenTemplate,
    templateType: messageTemplate.template_type,
  });

  const trackShare = (slug: string) => {
    return () => {
      void createRestApi(`/g/${slug}/share/web`).post({
        headers: {
          accept: 'application/json',
        },
        credentials: 'same-origin',
      });
    };
  };

  useEffect(() => {
    const bootstrap = async () => {
      const store = await configureStore<Required<ReduxState>>({
        combineReducers,
        transformer: (state) => state,
        reducers: {
          root: rootReducer,
          video: videoReducer,
          comments: commentsReducer,
          tracking: trackingReducer,
        },
        initialState: {
          comments: {
            comments: replies || [],
            creating: false,
            value: '',
            errorMessage: null,
            focused: false,
          },
          video: {
            playing: false,
            progress: 0,
            error: undefined,
          },
          root: {
            model: greet,
            assignee,
            messageTemplate,
            profile: {
              id: profile?.id,
              name: profile?.name,
              first_name: profile?.first_name,
              last_name: profile?.last_name,
              image: profile?.image,
            },
            lang,
            isRecipient,
            reactionCount,
            tracked: false,
            features,
            url: `${window.location.origin}/g/${greet.slug}`,
            replyByVideo,
            type: LandingPageType.Greet,
            metadata,
            greetType,
            intent,
          },
          tracking: {
            shareModalClicked: () =>
              analytics.track(
                GREET_SHARED,
                {
                  greetType,
                  templateType: messageTemplate.template_type,
                  sourceCreatedBy: metadata?.source_created_by,
                  deliveryType: metadata?.delivery_type,
                },
                null,
                trackShare(greet.slug)
              ),
            videoWatched: () =>
              analytics.track(GREET_VIEWED, {
                greetType,
                isRecipient,
                templateType: messageTemplate.template_type,
                sourceCreatedBy: metadata?.source_created_by,
                deliveryType: metadata?.delivery_type,
                emailPreview: metadata?.email_preview,
                context: 'landing_page',
              }),
            videoEmbedPlayed: () =>
              analytics.track(GREET_VIDEO_EMBED_PLAYED, {
                greetType,
                sourceCreatedBy: metadata?.source_created_by,
                deliveryType: metadata?.delivery_type,
              }),
            ctaClicked: () =>
              analytics.track(GREET_CTA_CLICKED, {
                templateType: messageTemplate.template_type,
                ctaText: messageTemplate.cta.text,
                ctaUrl: messageTemplate.cta.trueUrl,
                ctaColor: messageTemplate.cta.color,
                context: 'landing_page',
              }),
          },
        },
      });
      setStore(store);
    };
    void bootstrap();
  }, []);

  if (!store) return null;

  const component = (
    showMobileLayout 
      ? <Mobile /> 
      : showNextGenLayout 
        ? <NextGen 
            slug={greet.slug} 
            hideReport={hideReport} 
            showSingleColumnLayout={showSingleColumnLayout}
          />
        : <Postcard
            slug={greet.slug}
            showShareButtons={showShareButton}
          />
  );

  return (
    <FeaturesContext.Provider value={features}>
      <Provider store={store}>
        {component}
      </Provider>
    </FeaturesContext.Provider>
  );
}
